import { useEffect, useRef, useState } from "react";
import { useContent } from "../contexts/ContentContext";
import { useEditMode } from "../contexts/EditModeContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * <T k="namespace.key" as="span"> Default text </T>
 *
 * - Renders the override from CMS if it exists, otherwise the children string.
 * - When admin is logged in and edit mode is ON: turns into a contentEditable
 *   (plaintext-only) span with a dashed violet outline. Saves to backend on blur.
 * - For visitors and admins with edit mode OFF: renders as plain inline text.
 *
 * Pass a single string as children (multi-line via "\n" supported via white-space pre-line).
 */
export default function T({ k, as: Tag = "span", children, className = "", ...rest }) {
    const ctx = useContent();
    const editCtx = useEditMode();
    const auth = useAuth();
    const ref = useRef(null);
    const [saving, setSaving] = useState(false);

    const fallback = typeof children === "string" ? children : "";
    const value = ctx?.get?.(k, fallback) ?? fallback;

    const editable = !!auth?.user && !!editCtx?.editMode;

    // Sync DOM when value changes (e.g., after refresh)
    useEffect(() => {
        if (!editable) return;
        if (ref.current && ref.current.innerText !== value) {
            ref.current.innerText = value;
        }
    }, [editable, value]);

    if (!editable) {
        return (
            <Tag
                className={className}
                style={{ whiteSpace: "pre-line" }}
                {...rest}
            >
                {value}
            </Tag>
        );
    }

    const onBlur = async (e) => {
        const next = e.currentTarget.innerText.replace(/\u00A0/g, " ");
        if (next === value) return;
        setSaving(true);
        try {
            await ctx.setContent(k, next);
            toast.success("Gespeichert", { duration: 1200 });
        } catch (err) {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Tag
            ref={ref}
            contentEditable
            suppressContentEditableWarning
            spellCheck={false}
            onBlur={onBlur}
            data-cms-key={k}
            className={`${className} ${
                saving ? "opacity-60" : ""
            } outline-dashed outline-2 outline-offset-[6px] outline-aimploy-violet/40 hover:outline-aimploy-violet focus:outline-aimploy-violet rounded-sm transition-all cursor-text`}
            style={{ whiteSpace: "pre-line" }}
            {...rest}
        >
            {value}
        </Tag>
    );
}
